// Account.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useFollowManager } from './FollowManager';
import FollowList from './FollowList';

const Account = ({ relays }) => {
  const { publicKey, privateKey, userMetadata, logout } = useAuth();
  const { following, followers } = useFollowManager(relays);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const [showFollowingList, setShowFollowingList] = useState(false);
  const [showFollowersList, setShowFollowersList] = useState(false);
  const navigate = useNavigate();

  // Redirect if not logged in
  useEffect(() => {
	if (!publicKey) {
	  navigate('/login');
	}
  }, [publicKey, navigate]);

  const handleCopy = async (text, type) => {
	try {
	  await navigator.clipboard.writeText(text);
	  setCopyMessage(`${type} copied!`);
	  setTimeout(() => setCopyMessage(''), 2000);
	} catch (err) {
	  console.error('Failed to copy:', err);
	}
  };

  if (!publicKey) return null;

  return (
	<div className="account-page">
	  <div className="account-section">
		<h2>Your Profile</h2>
		<div className="profile-preview">
		  {userMetadata?.picture && (
			<img 
			  src={userMetadata.picture} 
			  alt="Profile" 
			  className="profile-picture-large"
			  onError={(e) => e.target.style.display = 'none'}
			/>
		  )}
		  <div className="profile-info">
			<h3>{userMetadata?.name || userMetadata?.display_name || 'Anonymous'}</h3>
			{userMetadata?.nip05 && (
			  <div className="nip05">{userMetadata.nip05}</div>
			)}
			{userMetadata?.about && (
			  <p className="about">{userMetadata.about}</p>
			)}
		  </div>
		</div>

		<div className="follow-stats">
		  <button
			className="follow-stat-button"
			onClick={() => setShowFollowingList(true)}
		  >
			<span className="stat-count">{following.size}</span>
			<span className="stat-label">Following</span>
		  </button>
		  <button
			className="follow-stat-button"
			onClick={() => setShowFollowersList(true)}
		  >
			<span className="stat-count">{followers.size}</span>
			<span className="stat-label">Followers</span>
		  </button>
		</div>

		<div className="account-actions">
		  <button 
			className="action-button primary"
			onClick={() => navigate('/profile-setup')}
		  >
			Edit Profile
		  </button>
		</div>
	  </div>

	  <div className="account-section">
		<h2>Account Keys</h2>
		<div className="key-section">
		  <div className="key-item">
			<label>Public Key:</label>
			<div className="key-display">
			  <code>{publicKey}</code>
			  <button 
				className="copy-button"
				onClick={() => handleCopy(publicKey, 'Public key')}
			  >
				📋
			  </button>
			</div>
		  </div>

		  <div className="key-item">
			<label>Private Key:</label>
			<div className="key-display">
			  <code>
				{showPrivateKey ? privateKey : '•'.repeat(40)}
			  </code>
			  <button 
				className="copy-button"
				onClick={() => handleCopy(privateKey, 'Private key')}
			  >
				📋
			  </button>
			  <button 
				className="reveal-button"
				onClick={() => setShowPrivateKey(!showPrivateKey)}
			  >
				{showPrivateKey ? '🫣' : '👁️'}
			  </button>
			</div>
			<div className="key-warning">
			  <p><strong>⚠️ Important:</strong> Your private key is your password. Keep it safe!</p>
			  <p>We recommend using a password manager like <a href="https://proton.me/pass" target="_blank" rel="noopener noreferrer">Proton Pass</a> to securely store your private key.</p>
			</div>
		  </div>
		  
		  {copyMessage && (
			<div className="copy-message">
			  {copyMessage}
			</div>
		  )}
		</div>
	  </div>

	  <div className="account-section">
		<h2>Account Actions</h2>
		<div className="danger-zone">
		  <button 
			className="action-button danger"
			onClick={() => {
			  logout();
			  navigate('/');
			}}
		  >
			Logout
		  </button>
		</div>
	  </div>

	  {showFollowingList && (
		<FollowList
		  users={Array.from(following)}
		  onClose={() => setShowFollowingList(false)}
		  title="Following"
		  relays={relays}
		/>
	  )}

	  {showFollowersList && (
		<FollowList
		  users={Array.from(followers)}
		  onClose={() => setShowFollowersList(false)}
		  title="Followers"
		  relays={relays}
		/>
	  )}
	</div>
  );
};

export default Account;